
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper,
    Alert,
    Chip,
    Skeleton
} from '@mui/material';
import {
    Favorite as FavoriteIcon,
    KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import contentService from '../services/contentService';
import ContentCard from './ContentCard';
import { useAuth } from '../context/AuthContext';
import { shareContentLink } from '../utils/shareUtils';
import '../styles/ProtectedRoutes.css';

const LikedPosts = () => {
    const [likedContent, setLikedContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const { user } = useAuth();
    const navigate = useNavigate();

    const loadLikedContent = async (reset = false) => {
        if (!user || user.role !== 'Customer') {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const currentOffset = reset ? 0 : offset;

            // Use the new /me endpoint which handles customer lookup automatically
            const response = await contentService.getLikedContent(20, currentOffset);

            if (response.success) {
                if (reset) {
                    setLikedContent(response.data);
                } else {
                    setLikedContent(prev => [...prev, ...response.data]);
                }
                setHasMore(response.pagination.hasMore);
                setOffset(currentOffset + response.data.length);
            } else {
                console.error('Failed to load liked posts:', response.message);
                // Only show error toast on initial load failure
                if (reset && likedContent.length === 0) {
                    toast.error('Failed to load liked posts');
                }
            }
        } catch (error) {
            console.error('Load liked content error:', error);
            // Only show error toast on initial load failure
            if (reset && likedContent.length === 0) {
                toast.error(error.message || 'Failed to load liked posts');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const loadInitialContent = async () => {
            if (isMounted && user && user.role === 'Customer') {
                await loadLikedContent(true);
            } else if (isMounted) {
                setLoading(false);
            }
        };

        // Only load if user is defined (not null/undefined)
        if (user !== undefined) {
            loadInitialContent();
        }

        return () => {
            isMounted = false;
        };
    }, [user]); // Add user dependency to prevent issues when user changes

    const handleLike = async (contentId) => {
        try {
            const response = await contentService.toggleLike(contentId);
            if (response.success) {
                // If unliked, remove from liked posts list
                if (response.data.action === 'unliked') {
                    setLikedContent(prev => prev.filter(item => item.ContentId !== contentId));
                    toast.success('Post removed from liked posts');
                } else {
                    // Update like count if still liked
                    setLikedContent(prev => prev.map(item =>
                        item.ContentId === contentId
                            ? {
                                ...item,
                                LikeCount: response.data.likeCount,
                                IsLikedByUser: 1
                            }
                            : item
                    ));
                }
            } else {
                toast.error(response.message || 'Failed to update like status');
            }
        } catch (error) {
            console.error('Like error:', error);
            toast.error(error.message || 'Failed to update like status');
        }
    };

    const handleComment = async (contentId, commentText) => {
        try {
            const response = await contentService.addComment(contentId, commentText);
            if (response.success) {
                setLikedContent(prev => prev.map(item =>
                    item.ContentId === contentId
                        ? {
                            ...item,
                            CommentCount: response.data?.commentCount
                                ?? (parseInt(item.CommentCount || 0) + 1)
                        }
                        : item
                ));
                toast.success('Comment added successfully');
                return true;
            } else {
                toast.error(response.message || 'Failed to add comment');
                return false;
            }
        } catch (error) {
            console.error('Comment error:', error);
            toast.error(error.message || 'Failed to add comment');
            return false;
        }
    };

    const handleShare = async (contentId) => {
        try {
            const response = await contentService.shareContent(contentId, 'link');
            if (response.success) {
                const shareResult = await shareContentLink(contentId, {
                    title: 'Check this post',
                    text: 'Take a look at this post on Shipway'
                });

                setLikedContent(prev => prev.map(item =>
                    item.ContentId === contentId
                        ? { ...item, ShareCount: response.data.shareCount }
                        : item
                ));
            } else {
                toast.error(response.message || 'Failed to share post');
            }
        } catch (error) {
            console.error('Share error:', error);
            toast.error(error.message || 'Failed to share post');
        }
    };

    const handleDelete = (contentId) => {
        // Remove the deleted post from liked posts
        setLikedContent(prevContent => prevContent.filter(item => item.ContentId !== contentId));
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            loadLikedContent(false);
        }
    };

    const handleUploaderClick = (uploaderId) => {
        // Navigate to uploader's profile
        navigate(`/profile/${uploaderId}`);
    };

    if (!user || user.role !== 'Customer') {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper
                    elevation={0}
                    className="glass-content-container"
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 3,
                        border: '1px solid rgba(188, 150, 94, 0.25)'
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(188, 150, 94, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                        }}
                    >
                        <FavoriteIcon sx={{ color: '#bc965e', fontSize: 30 }} />
                    </Box>
                    <Typography variant="h6" gutterBottom className="glass-title">
                        Access Restricted
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        Please login as a customer to view your liked posts.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (loading && likedContent.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Skeleton
                    variant="rounded"
                    height={90}
                    sx={{ borderRadius: 3, mb: 3, bgcolor: 'rgba(188, 150, 94, 0.08)' }}
                />
                <Box sx={{ columnCount: { xs: 1, sm: 1, md: 3 }, columnGap: '16px' }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={280 + (i % 3) * 40}
                            sx={{ borderRadius: 3, mb: 2, breakInside: 'avoid', bgcolor: 'rgba(188, 150, 94, 0.08)' }}
                        />
                    ))}
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Hero Header */}
       
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                    <FavoriteIcon sx={{ color: '#bc965e', fontSize: 28 }} />
                    <Typography
                        variant="h4"
                        className="glass-title"
                        sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}
                    >
                        Your Liked Posts
                    </Typography>
                </Box>
                <Typography variant="body2" className="glass-text-secondary" sx={{mb:3}}>
                    Posts you've liked will appear here
                </Typography>
         

            {/* Masonry-style Content Grid */}
            {/* Content Grid */}
            {likedContent.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: '1fr',
                            md: 'repeat(2, 1fr)',
                             lg: 'repeat(3, 1fr)'
                        },
                        gap: '16px',
                        alignItems: 'start'
                    }}
                >
                    {likedContent.map((item) => (
                        <Box key={item.ContentId}>
                            <ContentCard
                                content={item}
                                onLike={handleLike}
                                onComment={handleComment}
                                onShare={handleShare}
                                currentUser={user}
                                onUploaderClick={handleUploaderClick}
                                onDelete={handleDelete}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {/* Load More */}
            {hasMore && likedContent.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        onClick={loadMore}
                        disabled={loading}
                        endIcon={loading ? null : <ArrowDownIcon />}
                        sx={{
                            px: 4,
                            py: 1,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#bc965e',
                            border: '1px solid rgba(188, 150, 94, 0.4)',
                            backgroundColor: 'rgba(188, 150, 94, 0.06)',
                            '&:hover': {
                                backgroundColor: 'rgba(188, 150, 94, 0.15)',
                                borderColor: '#bc965e'
                            }
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} sx={{ color: '#bc965e' }} />
                                Loading...
                            </Box>
                        ) : (
                            'Load More'
                        )}
                    </Button>
                </Box>
            )}

            {/* End Message */}
            {!hasMore && likedContent.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Chip
                        label="You've seen all your liked posts"
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            color: 'rgba(255,255,255,0.6)'
                        }}
                    />
                </Box>
            )}

            {/* Empty State */}
            {likedContent.length === 0 && !loading && (
                <Paper
                    elevation={0}
                    className="glass-empty-state"
                    sx={{
                        textAlign: 'center',
                        p: { xs: 4, sm: 6 },
                        borderRadius: 4,
                        border: '1px dashed rgba(188, 150, 94, 0.3)'
                    }}
                >
                    <Box sx={{ mb: 2, fontSize: '3.5rem', lineHeight: 1 }}>💝</Box>
                    <Typography variant="h6" gutterBottom className="glass-text-primary" sx={{ fontWeight: 600 }}>
                        No liked posts yet
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary" sx={{ maxWidth: 340, mx: 'auto' }}>
                        Posts you like will appear here. Start exploring and like some posts!
                    </Typography>
                </Paper>
            )}
        </Container>
    );
};

export default LikedPosts;